import { cn } from "@iec-id/ui";
import { Outlet } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { DeletionBanner } from "@/components/legal/deletion-banner";
import { formatDate } from "@/lib/format";
import { applyProfileLocale } from "@/lib/i18n";
import { useRealtime } from "@/lib/realtime";
import { useStickyColumn } from "@/lib/sticky-column";
import { api } from "@/lib/trpc";
import { AppHeader } from "./app-header";
import { BackLink } from "./back-link";
import { SessionGate } from "./session-gate";
import { SideNav } from "./side-nav";
import { useSignOut, useThemeSync } from "./use-shell";

/**
 * Оболочка витрины: канва 10px по краям, шапка 64px, меню 220px, зазор между
 * карточками 4px (макет 2a). На мобильном — стеклянная шапка 56px, поля 6px
 * и бургер с левой шторкой (макет 2b).
 *
 * Здесь же единственная точка подписки на Socket.IO: сокет живёт ровно столько,
 * сколько открыта витрина, и не пересоздаётся при смене раздела.
 */
export function AppShell() {
	return (
		<SessionGate>
			<ShowcaseShell />
		</SessionGate>
	);
}

/**
 * Оболочка после гейта. Запросы разделов стартуют только здесь — иначе
 * анонимный заход бьёт API десятком 401 ещё до ухода на вход.
 */
function ShowcaseShell() {
	const { t, i18n } = useTranslation("common");
	const { signOut } = useSignOut();
	useRealtime();
	useThemeSync();

	const navRef = useStickyColumn<HTMLDivElement>();

	const links = api.links.list.useQuery({});
	const entitlements = api.entitlements.mine.useQuery();
	const profile = api.profile.get.useQuery();

	// Язык профиля — то, что человек выбрал за себя, а не то, что стоит в
	// браузере, из которого он вошёл сегодня. Подставляем сразу после входа.
	const profileLocale = profile.data?.locale;
	useEffect(() => {
		applyProfileLocale(profileLocale);
	}, [profileLocale]);

	const tier = entitlements.data?.effectiveTier ?? "free";
	const nearestExpiry = entitlements.data?.nearestExpiresAt ?? null;

	return (
		<div className="min-h-screen bg-page">
			<a
				href="#main"
				className={cn(
					"sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-100",
					"focus:rounded-control focus:bg-card focus:px-4 focus:py-2 focus:t-body",
				)}
			>
				{t("header.skipToContent")}
			</a>

			<div className="lg:px-2.5 lg:pb-2.5">
				{/* Шапка едет со страницей: в ней Баллы, уведомления и меню
				    аккаунта — на длинной странице (Баллы, Сервисы) до них
				    приходилось скроллить обратно наверх.

				    Липкость живёт ЗДЕСЬ, на обёртке, и на всех ширинах. Своё
				    `sticky` у мобильной шапки не работало: элемент липнет в
				    пределах родителя, а этот родитель ровно её высоты — запас
				    хода нулевой, и шапка уезжала вверх вместе с ним. На
				    десктопе обёртка держит ещё фон страницы и верхний отступ,
				    иначе в зазор 10px просвечивали бы проезжающие карточки. */}
				<div className="sticky top-0 z-50 lg:bg-page lg:pt-2.5">
					<AppHeader
						onSignOut={signOut}
						navMeta={{
							plus:
								tier === "free"
									? t("tier.free")
									: nearestExpiry
										? `${t(`tier.${tier}`)} · ${formatDate(nearestExpiry, i18n.language)}`
										: t(`tier.${tier}`),
							services: links.data?.items.length,
						}}
					/>
				</div>

				<div className="mx-auto mt-0 flex max-w-[1200px] items-start gap-2.5 px-1.5 lg:mt-2.5 lg:px-2.5">
					{/* Меню зафиксировано под шапкой: на длинных разделах (Баллы,
					    Сервисы) скроллится только содержимое, а разделы остаются на
					    месте. Упоры и вся арифметика — в `useStickyColumn`; если меню
					    не помещается в экран (низкое окно, длинные подписи на de/hu),
					    оно доезжает до своего низа вместе со страницей, а не прячет
					    нижние разделы за краем экрана.

					    `shrink-0` держит ширину колонки явно, как и у боковых колонок
					    разделов: `w-[220px]` внутреннего `<nav>` не спасает, потому
					    что сжимается внешний flex-элемент, а не он. */}
					<div ref={navRef} className="hidden lg:sticky lg:block lg:shrink-0">
						<SideNav />
					</div>

					<main
						id="main"
						// Якорь для браузерных сценариев: `main` есть и на экранах входа,
						// поэтому «витрина открылась у вошедшего» по нему не отличить.
						data-testid="showcase-main"
						className="flex min-w-0 flex-grow flex-col gap-1 py-1.5 lg:gap-1 lg:py-0"
					>
						{/* Отсрочка удаления видна на любом разделе: это последнее окно,
						    в котором решение обратимо, и прятать его в «Правовом центре»
						    значит показывать только тому, кто и так туда шёл. */}
						<DeletionBanner />
						{/* Возврат на уровень выше — только на телефоне: на десктопе
						    та же навигация всегда видна левым меню. */}
						<BackLink />
						<Outlet />
					</main>
				</div>
			</div>
		</div>
	);
}
